import React from 'react'
import type { FfpDashboardData, MeasureKey, PeriodKey } from '../../types'
import { bandRanges, formatPercent, formatRands, longMonth, monthOf, sliceSeries } from '../../utils'
import { CohortTable, PanelFreshness, PanelNote, PanelSection, PanelTable, PanelText } from '../AboutPanel'
import { PanelIncluded } from '../PanelIncluded'
import { AVERAGE_CALC, BAND_NAMES, FRESHNESS_SOURCE, MEASURES, MONEY_PARTS, moneySplits, RETIREMENT, ZERO_INCOME } from '../../copy'

export type HealthPanel = 'money' | MeasureKey | 'retirement'

export const PANEL_TITLES: Record<HealthPanel, string> = {
  money: 'Where your team’s money goes',
  debt: MEASURES.debt.name,
  spending: MEASURES.spending.name,
  investing: MEASURES.investing.name,
  retirement: RETIREMENT.name,
}

type Props = {
  panel: HealthPanel | null
  data: FfpDashboardData
  period: PeriodKey
}

export const HealthPanelContent = ({ panel, data, period }: Props) => {
  if (panel === null) return null
  if (panel === 'money') return <MoneyPanel data={data} />
  if (panel === 'retirement') return <RetirementPanel data={data} period={period} />
  return <TrendPanel measure={panel} data={data} period={period} />
}

const pending = (count: number) => ({
  count,
  label: 'Pending: no retirement score yet',
  detail: 'They’ve onboarded, but their retirement readiness score hasn’t come through yet. They’ll appear once it does.',
})

type IncludedProps = {
  data: FfpDashboardData
  people: number
  excluded: number
  pendingCount?: number
}

// Everyone onboarded is either counted, excluded, pending or not yet in the
// latest capture. "Nobody is left out" only when the first three add up.
const WhoIsIncluded = ({ data, people, excluded, pendingCount = 0 }: IncludedProps) => {
  const { onboarded } = data.people
  const uncaptured = onboarded - people - excluded - pendingCount
  const captured = monthOf(data.freshness.captured_at)
  const notCaptured = {
    count: uncaptured,
    label: captured ? `No capture for ${longMonth(captured)} yet` : 'Not captured yet',
    detail: 'They joined after the last weekly capture, or the capture skipped them.',
  }

  return (
    <PanelSection heading="Who is included">
      <PanelIncluded
        included={`${people} people`}
        of={`of ${onboarded} onboarded`}
        exclusions={[{ count: excluded, ...ZERO_INCOME }, pending(pendingCount), notCaptured]}
        everyone={uncaptured === 0 ? 'Nobody onboarded is left out.' : undefined}
      />
    </PanelSection>
  )
}

const Freshness = ({ at }: { at: string | null }) => (
  <PanelSection heading="Where the numbers come from">
    <PanelFreshness source={FRESHNESS_SOURCE} at={at} />
    <PanelNote>Our data updates every Monday.</PanelNote>
  </PanelSection>
)

const Benchmark = ({ text }: { text: string }) => (
  <PanelSection heading="Benchmark">
    <PanelText>{text}</PanelText>
  </PanelSection>
)

const MoneyPanel = ({ data }: { data: FfpDashboardData }) => {
  const { money, trends } = data.health
  const splits = moneySplits(money.first, money.latest)
  const columns = ['Of every R100', ...splits.map((split) => longMonth(split.month))]
  const rows = MONEY_PARTS.map((part) => [part.name, ...splits.map((split) => formatRands(split[part.key]))])

  return (
    <>
      <PanelSection heading="What it means">
        <PanelText>
          For each person, we work out what share of their pay before tax goes to spending, to debt repayments and to investing, then average those shares across
          everyone included. Spending already includes debt repayments and investing, so the shares overlap. We don’t have tax data, so tax and other deductions sit in “Everything
          else”.
        </PanelText>
        <PanelText muted>
          Because these are averages, a few people with very high shares can pull a figure up. The cards below also show how many people are within each guide.
        </PanelText>
      </PanelSection>
      <WhoIsIncluded data={data} people={money.people} excluded={money.excluded} />
      <PanelSection heading="The numbers behind the card">
        <PanelTable columns={columns} rows={rows} />
      </PanelSection>
      <Freshness at={data.freshness.captured_at} />
      <Benchmark
        text={`Guides used elsewhere on this page: debt ${trends.debt.guide}% or less, spending ${trends.spending.guide}% or less, investing over ${trends.investing.guide}%.`}
      />
    </>
  )
}

const TrendPanel = ({ measure, data, period }: { measure: MeasureKey; data: FfpDashboardData; period: PeriodKey }) => {
  const trend = data.health.trends[measure]
  const copy = MEASURES[measure]
  const rows = sliceSeries(trend.series, period).map((point) => [longMonth(point.month), ...monthCells(point.people, point.value === null ? null : [formatPercent(point.value)])])

  return (
    <>
      <PanelSection heading="What it measures">
        <PanelText>{copy.measures}</PanelText>
        <PanelText muted>{`${copy.about} ${copy.guideNote(trend.guide)}`}</PanelText>
      </PanelSection>
      <PanelSection heading="What it means">
        <PanelText>{copy.definition}</PanelText>
        <PanelText muted>{AVERAGE_CALC}</PanelText>
      </PanelSection>
      <WhoIsIncluded data={data} people={trend.people} excluded={trend.excluded} />
      <PanelSection heading="The numbers behind the card">
        <PanelTable columns={TREND_COLUMNS} rows={rows} />
        <WithinGuide measure={measure} data={data} />
      </PanelSection>
      <PanelSection heading="By cohort">
        <CohortTable rows={data.health.cohorts[measure]} notInCohort={data.health.cohorts.not_in_cohort} valueHeading="Average" formatValue={formatPercent} />
      </PanelSection>
      <Freshness at={data.freshness.captured_at} />
      <Benchmark text={copy.benchmark(trend.guide)} />
    </>
  )
}

// The shares behind the card's takeaway, first capture and latest.
const WithinGuide = ({ measure, data }: { measure: MeasureKey; data: FfpDashboardData }) => {
  const { first, latest } = data.health.trends[measure].within_guide
  if (!first || !latest) return null

  return (
    <PanelText muted>
      Within the guide: {first.share}% in {longMonth(first.month)}, {latest.share}% in {longMonth(latest.month)}.
    </PanelText>
  )
}

const TREND_COLUMNS = ['Month', 'People', 'Average']
const RETIREMENT_COLUMNS = ['Month', 'People', ...BAND_NAMES]

// A month's people and figures. Under 5 people the figures are withheld (and
// the exact count with them); nobody captured is no data.
const monthCells = (people: number, figures: string[] | null, width = 1): string[] => {
  const blanks = Array<string>(width - 1).fill('')
  if (people === 0) return ['0', 'No data', ...blanks]
  if (figures === null) return ['Fewer than 5', 'Hidden: fewer than 5', ...blanks]
  return [String(people), ...figures]
}

const RetirementPanel = ({ data, period }: { data: FfpDashboardData; period: PeriodKey }) => {
  const bands = data.health.retirement_bands
  const ranges = bandRanges(bands.bands)
  const bandList = BAND_NAMES.map((name, index) => `${name} (${ranges[index]})`).join(', ')
  const rows = sliceSeries(bands.series, period).map((point) => [
    longMonth(point.month),
    ...monthCells(point.people, point.shares ? point.shares.map((share) => formatPercent(share)) : null, BAND_NAMES.length),
  ])

  return (
    <>
      <PanelSection heading="What it measures">
        <PanelText>{RETIREMENT.metric}</PanelText>
        <PanelText muted>{RETIREMENT.meaning}</PanelText>
      </PanelSection>
      <PanelSection heading="What it means">
        <PanelText>
          Every person gets a retirement readiness score in the Wealthbit app. It’s a percentage based on their age, savings and what they put away each month. We
          group scores into five bands: {bandList}.
        </PanelText>
        <PanelText muted>Each month we count how many people sit in each band, as a share of everyone included that month.</PanelText>
      </PanelSection>
      <WhoIsIncluded data={data} people={bands.people} excluded={bands.excluded} pendingCount={bands.pending} />
      <PanelSection heading="The numbers behind the card">
        <PanelTable columns={RETIREMENT_COLUMNS} rows={rows} />
      </PanelSection>
      <PanelSection heading="By cohort">
        <CohortTable
          rows={data.health.cohorts.retirement}
          notInCohort={data.health.cohorts.not_in_cohort}
          valueHeading="Likely or highly likely"
          formatValue={formatPercent}
        />
      </PanelSection>
      <Freshness at={data.freshness.captured_at} />
      <Benchmark text="Band thresholds match the current employer dashboard. A person’s own plan in the Wealthbit app uses different ones." />
    </>
  )
}
